// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { get } from './api';
import { ENDPOINTS } from '../utils/constants';
import type { Artifact, PagedArtifacts } from '../types';

export interface ListSopsFilters {
  artifactType?: string;
  domain?: string;
  equipmentType?: string;
  procedureType?: string;
  riskTier?: string;
  status?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

const FILTER_TO_QUERY_PARAM: Record<keyof ListSopsFilters, string> = {
  artifactType: 'artifact_type',
  domain: 'domain',
  equipmentType: 'equipment_type',
  procedureType: 'procedure_type',
  riskTier: 'risk_tier',
  status: 'status',
  q: 'q',
  page: 'page',
  pageSize: 'page_size',
};

/**
 * Where SOPs are read from. Omitted: the SOP Factory catalog (global). With a
 * `siteId`: ADM, the same artifacts scoped to that site (AB#33803) — callers
 * pass it only while `adm-sop-catalog-enabled` is on.
 */
export interface SopSource {
  siteId: string;
}

/**
 * List catalog artifacts. Defaults to `artifact_type=sop` so callers get SOPs
 * unless they override it. Extra facets map camelCase → snake_case query params.
 */
export const listSops = (
  filters: ListSopsFilters = {},
  source?: SopSource
): Promise<PagedArtifacts> => {
  const merged: ListSopsFilters = { artifactType: 'sop', ...filters };
  const params = new URLSearchParams();
  (Object.keys(merged) as (keyof ListSopsFilters)[]).forEach(key => {
    const value = merged[key];
    if (value !== undefined && value !== null && value !== '') {
      params.set(FILTER_TO_QUERY_PARAM[key], String(value));
    }
  });
  const qs = params.toString();
  const base = source
    ? ENDPOINTS.ADM_SITE_ARTIFACTS(source.siteId)
    : ENDPOINTS.SOP_FACTORY_ARTIFACTS;
  return get<PagedArtifacts>(qs ? `${base}?${qs}` : base);
};

/**
 * Fetch a single artifact (SOP or policy) by its stable id.
 *
 * Optional resource: callers (useAlarms.loadAlarmWithSop, the SOP detail page)
 * already degrade gracefully when it can't be resolved, so a 401 from the SOP
 * Factory must not tear down the user's session — it means that one upstream
 * rejected our token, not that we're logged out. Without this, an alarm whose
 * linked SOP 401s logged the user out of the whole app on every visit to the
 * detail page.
 */
export const getArtifact = (id: string, source?: SopSource): Promise<Artifact | null> =>
  get<Artifact | null>(
    source
      ? ENDPOINTS.ADM_SITE_ARTIFACT_BY_ID(source.siteId, id)
      : ENDPOINTS.SOP_FACTORY_ARTIFACT_BY_ID(id),
    { endSessionOn401: false }
  );
